import { VariableTypes, type VariableTypeName } from './variableTypes';
import { VariationTypes } from './variationTypes';

type FeatureFlagBase = {
  // Determines whether the feature flag is send to Fullstory as a user property
  fullstoryEnabled?: boolean;
  name: string;
  variables?: Record<string, VariableTypeName>;
};

export type RolloutFeatureFlag = {
  variationType: typeof VariationTypes.ROLLOUT;
} & FeatureFlagBase;

export type ABTestingFeatureFlag = {
  variationType: typeof VariationTypes.A_B_TESTING;
} & FeatureFlagBase;

export type CustomFeatureFlag = {
  variationType: typeof VariationTypes.CUSTOM;
  variations: readonly string[];
} & FeatureFlagBase;

export type FeatureFlag = ABTestingFeatureFlag | CustomFeatureFlag | RolloutFeatureFlag;

// Names must match the feature flag names in Optimizely
export const FeatureFlags = {
  AMAZON_SHIPPING_ACCOUNT_CONNECTION: {
    fullstoryEnabled: true,
    name: 'amazon_shipping_account_connection',
    variationType: VariationTypes.ROLLOUT,
  },
  AMAZON_SHIPPING_ACCOUNT_REGISTRATION: {
    fullstoryEnabled: true,
    name: 'amazon_shipping_account_registration',
    variationType: VariationTypes.ROLLOUT,
  },
  AUTOMATION_RECOMMENDATIONS: {
    fullstoryEnabled: true,
    name: 'automation_recommendations',
    variationType: VariationTypes.ROLLOUT,
  },
  BEST_RATES_FREQUENCY: {
    fullstoryEnabled: true,
    name: 'best_rates_frequency',
    variables: {
      best_rates_threshold: VariableTypes.DOUBLE,
    },
    variationType: VariationTypes.ROLLOUT,
  },
  BFA_INVOICE_PAYMENT_DETAILS_UNION_QUERY: {
    fullstoryEnabled: true,
    name: 'bfa_invoice_payment_details_union_query',
    variationType: VariationTypes.ROLLOUT,
  },
  BFI_BUSINESS_TYPE_FOR_TOKENS: {
    fullstoryEnabled: true,
    name: 'bfi_business_type_for_tokens',
    variationType: VariationTypes.ROLLOUT,
  },
  BFI_FRAUD_SCORE_GATE_API_KEY_GENERATION: {
    fullstoryEnabled: true,
    name: 'bfi_fraud_score_gate_api_key_generation',
    variationType: VariationTypes.ROLLOUT,
  },
  BILLING_PAYMENT_FAILURE_PURCHASE_BLOCK: {
    fullstoryEnabled: true,
    name: 'billing_payment_failure_purchase_block',
    variationType: VariationTypes.ROLLOUT,
  },
  CCAP_AMAZON_SHIPPING_PICKUP_ENABLED: {
    fullstoryEnabled: true,
    name: 'ccap_amazon_shipping_pickup_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  DHL_GERMANY_SUPPORT_DEPRECATED: {
    fullstoryEnabled: false,
    name: 'carrier_support_deprecated',
    variationType: VariationTypes.ROLLOUT,
  },
  ESTIMATED_DELIVERY_DATE_EXPERIMENT_V2: {
    fullstoryEnabled: true,
    name: 'estimated_delivery_date_experiment_v2',
    variationType: VariationTypes.A_B_TESTING,
  },
  FEDEX_MCA_VERIFICATION: {
    fullstoryEnabled: false,
    name: 'fedex_account_object_info_verification_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  IBD_HIDE_IF_TRIED_AUTO_APPLY: {
    fullstoryEnabled: true,
    name: 'ibd_hide_if_tried_auto_apply',
    variationType: VariationTypes.ROLLOUT,
  },
  INSURANCE_LARGE_PACKAGES_UPSELL: {
    fullstoryEnabled: true,
    name: 'insurance_large_packages_upsell',
    variationType: VariationTypes.A_B_TESTING,
  },
  NEW_PACKAGE_OPTIONS_CARDS_ON_DRAWER: {
    fullstoryEnabled: true,
    name: 'new_package_options_cards_on_drawer',
    variationType: VariationTypes.ROLLOUT,
  },
  ONBOARDING_2026: {
    fullstoryEnabled: true,
    name: 'onboarding_2026',
    variationType: VariationTypes.A_B_TESTING,
  },
  OPEN_ORDER_DRAWER_ON_BACK: {
    fullstoryEnabled: false,
    name: 'open_order_drawer_on_back',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDER_DETAILS_PAGE_MUI: {
    fullstoryEnabled: true,
    name: 'order_details_page_mui',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDERS_API_V2_UI_VALIDATION: {
    name: 'orders_api_v2_ui_validation',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDERS_ITEMS_API_V2: {
    fullstoryEnabled: false,
    name: 'orders_items_api_v2',
    variationType: VariationTypes.ROLLOUT,
  },
  ORDERS_TABLE_API_V2_UI: {
    name: 'orders_table_api_v2_ui',
    variationType: VariationTypes.ROLLOUT,
  },
  PACKAGE_TEMPLATE_RECOMMENDATION: {
    fullstoryEnabled: true,
    name: 'package_template_recommendation',
    variationType: VariationTypes.CUSTOM,
    variations: ['prepurchase', 'postpurchase', 'off'],
  },
  QUANTITY_FILTER: {
    fullstoryEnabled: true,
    name: 'quantity_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  QUICKPACK_DEVICES: {
    fullstoryEnabled: true,
    name: 'quickpack_devices',
    variationType: VariationTypes.ROLLOUT,
  },
  REFERRAL_PROGRAM_EXPERIMENT: {
    fullstoryEnabled: true,
    name: 'referral_program_experiment',
    variationType: VariationTypes.ROLLOUT,
  },
  SENDER_ADDRESS_FILTER: {
    name: 'sender_address_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  SHIPPING_RISK_ASSESSMENT_EXPERIMENT_V2: {
    fullstoryEnabled: true,
    name: 'shipping_risk_assessment_experiment_v2',
    variationType: VariationTypes.A_B_TESTING,
  },
  SHOW_RETAIL_RATES_IN_ONBOARDING: {
    fullstoryEnabled: true,
    name: 'show_retail_rates_in_onboarding',
    variationType: VariationTypes.ROLLOUT,
  },
  SMART_RECOMMENDATIONS: {
    fullstoryEnabled: true,
    name: 'smart_recommendations',
    variationType: VariationTypes.ROLLOUT,
  },
  STORE_SPECIFIC_ORDER_FILTER: {
    name: 'store_specific_order_filter',
    variationType: VariationTypes.ROLLOUT,
  },
  TURN_ON_IBD_ENABLED: {
    fullstoryEnabled: true,
    name: 'turn_on_ibd_xcover_enabled',
    variationType: VariationTypes.ROLLOUT,
  },
  USPS_AES_ITN_UI: {
    fullstoryEnabled: true,
    name: 'usps_aes_itn_ui',
    variationType: VariationTypes.ROLLOUT,
  },
  WEBAPP_CARRIER_ADDRESS_VALIDATION_BYPASS: {
    fullstoryEnabled: true,
    name: 'webapp_carrier_address_validation_bypass',
    variationType: VariationTypes.A_B_TESTING,
  },
} as const satisfies Record<string, FeatureFlag>;

/**
 * Build a map of feature flags that are enabled for FullStory for easier access
 */
export const FullStoryFeatureFlags = Object.fromEntries(
  Object.entries(FeatureFlags as Record<string, FeatureFlag>).map(
    ([_, { fullstoryEnabled, name }]) => [name, fullstoryEnabled ?? false],
  ),
) as Record<string, boolean>;
